import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
  Alert,
} from '@mui/material';
import React, { useState } from 'react';

interface FederalEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FederalEmployeeFormData) => void;
}

interface FederalEmployeeFormData {
  fullName: string;
  emailAddress: string;
  agencyDepartment: string;
  jobTitle: string;
  location: string;
  subjectAreaOfInterest: string;
  policyFocusAreas: string[];
  yearsInPublicService: string;
  preferredContactMethod: string;
  interests: string[];
  message: string;
}

const POLICY_FOCUS_AREAS = [
  'Family Court Reform',
  'Child Custody Laws',
  'Parental Rights',
  'Judicial Accountability',
  'Child Support Reform',
  'Domestic Relations',
  'Civil Rights',
  'Constitutional Law',
  'Administrative Law',
  'Government Reform',
  'Ethics and Transparency',
  'Public Policy',
];

const CONTACT_METHODS = ['Email', 'Phone', 'Text Message', 'Video Call', 'In-Person Meeting'];

const INTEREST_OPTIONS = [
  'Receive updates on federal reform initiatives',
  'Join advocacy efforts',
  'Provide feedback on proposed legislation',
  'Participate in working groups',
  'Share professional expertise',
  'Connect with other federal employees',
  'Attend briefings and events',
  'Volunteer for campaign activities',
];

const FederalEmployeeDialog: React.FC<FederalEmployeeDialogProps> = ({ open, onClose, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState<FederalEmployeeFormData>({
    fullName: '',
    emailAddress: '',
    agencyDepartment: '',
    jobTitle: '',
    location: '',
    subjectAreaOfInterest: '',
    policyFocusAreas: [],
    yearsInPublicService: '',
    preferredContactMethod: '',
    interests: [],
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name as string]: value,
    }));
  };

  const handleMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name as string]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleCheckboxChange = (interest: string, checked: boolean) => {
    setFormData((prevData) => ({
      ...prevData,
      interests: checked ? [...prevData.interests, interest] : prevData.interests.filter((item) => item !== interest),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Collect form data with metadata
      const submissionData = {
        ...formData,
        ipAddress: '', // Will be filled by the function
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };

      // Send to Netlify function
      const response = await fetch('/.netlify/functions/send-federal-employee-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        onSubmit(formData);

        // Close dialog after a brief delay to show success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting federal employee application:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form and status
    setSubmitStatus('idle');
    setErrorMessage('');
    setIsSubmitting(false);

    // Reset form data
    setFormData({
      fullName: '',
      emailAddress: '',
      agencyDepartment: '',
      jobTitle: '',
      location: '',
      subjectAreaOfInterest: '',
      policyFocusAreas: [],
      yearsInPublicService: '',
      preferredContactMethod: '',
      interests: [],
      message: '',
    });
    onClose();
  };

  const isFormValid = () => {
    return (
      formData.fullName.trim() !== '' &&
      formData.emailAddress.trim() !== '' &&
      formData.agencyDepartment.trim() !== '' &&
      formData.subjectAreaOfInterest.trim() !== ''
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        Federal Employee Coalition
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Success Message */}
        {submitStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Application submitted successfully! We'll be in touch soon.
          </Alert>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            ❌ {errorMessage}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 3 }}>
          Join our coalition of federal employees working to advance family court reform and parental rights
          legislation. Your voice and expertise are crucial to building comprehensive federal solutions.
        </Typography>

        <Grid container spacing={2}>
          {/* Full Name - Required */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              fullWidth
              required
              error={formData.fullName.trim() === ''}
              helperText={formData.fullName.trim() === '' ? 'Full name is required' : ''}
            />
          </Grid>

          {/* Email Address - Required */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email Address"
              name="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={handleChange}
              fullWidth
              required
              error={formData.emailAddress.trim() === ''}
              helperText={formData.emailAddress.trim() === '' ? 'Email address is required' : ''}
            />
          </Grid>

          {/* Agency/Department - Required */}
          <Grid item xs={12}>
            <TextField
              label="Agency/Department"
              name="agencyDepartment"
              value={formData.agencyDepartment}
              onChange={handleChange}
              fullWidth
              required
              error={formData.agencyDepartment.trim() === ''}
              helperText={formData.agencyDepartment.trim() === '' ? 'Agency/Department is required' : ''}
              placeholder="e.g., Department of Justice, Health and Human Services, etc."
            />
          </Grid>

          {/* Job Title/Role - Optional */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Job Title/Role"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              fullWidth
              placeholder="e.g., Attorney, Policy Analyst, Program Manager"
            />
          </Grid>

          {/* Location - Optional */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              fullWidth
              placeholder="e.g., Washington D.C., Regional Office, Remote"
            />
          </Grid>

          {/* Subject/Area of Interest - Required */}
          <Grid item xs={12}>
            <TextField
              label="Subject/Area of Interest"
              name="subjectAreaOfInterest"
              value={formData.subjectAreaOfInterest}
              onChange={handleChange}
              fullWidth
              required
              error={formData.subjectAreaOfInterest.trim() === ''}
              helperText={formData.subjectAreaOfInterest.trim() === '' ? 'Subject/Area of Interest is required' : ''}
              placeholder="e.g., Family Court Reform, Child Welfare Policy, Legal Reform"
            />
          </Grid>

          {/* Policy Focus Area - Multi-select */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Policy Focus Areas</InputLabel>
              <Select
                multiple
                name="policyFocusAreas"
                value={formData.policyFocusAreas}
                onChange={handleMultiSelectChange}
                input={<OutlinedInput label="Policy Focus Areas" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {POLICY_FOCUS_AREAS.map((area) => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Years in Public Service - Optional */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Years in Public Service"
              name="yearsInPublicService"
              value={formData.yearsInPublicService}
              onChange={handleChange}
              fullWidth
              placeholder="e.g., 5, 10-15, 20+"
            />
          </Grid>

          {/* Preferred Contact Method */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Preferred Contact Method</InputLabel>
              <Select
                name="preferredContactMethod"
                value={formData.preferredContactMethod}
                onChange={handleSelectChange}
                label="Preferred Contact Method"
              >
                {CONTACT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Interest Checkboxes */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              How would you like to be involved?
            </Typography>
            <FormGroup>
              <Grid container>
                {INTEREST_OPTIONS.map((interest) => (
                  <Grid item xs={12} sm={6} key={interest}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interests.includes(interest)}
                          onChange={(e) => handleCheckboxChange(interest, e.target.checked)}
                          name={interest}
                        />
                      }
                      label={interest}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Grid>

          {/* Message Field */}
          <Grid item xs={12}>
            <TextField
              label="Additional Comments or Questions"
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              placeholder="Share any additional thoughts, questions, or ways you'd like to contribute to federal family court reform..."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Privacy Note:</strong> Your information will be used solely for coordinating federal reform efforts
            and will be kept confidential. We will not share your details without your explicit consent.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || isSubmitting || submitStatus === 'success'}
          sx={{ ml: 1 }}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting ? 'Submitting...' : submitStatus === 'success' ? 'Submitted!' : 'Join Coalition'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FederalEmployeeDialog;
